import React from 'react';
import InputWrapper from './InputWrapper';
import { InputState, Link } from './types';
import { CloseIcon, SearchIcon } from '../svg';
import { getOutlineColor } from './utils';
import TooltipComponent from '../tooltip';
import Error from '../svg/State/Error';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';

type InputAttributes = React.InputHTMLAttributes<HTMLInputElement>;

type Props = {
  label?: string;
  id: InputAttributes['id'];
  name?: InputAttributes['name'];
  subText?: string;
  placeholder?: InputAttributes['placeholder'];
  value?: InputAttributes['value'];
  helperText?: string;
  link?: Link;
  inputState?: InputState;
  disabled?: InputAttributes['disabled'];
  type?: InputAttributes['type'] | 'textarea';
  className?: string;
  icon?: React.ReactNode;
  isRequired?: boolean;
  floatLabel?: boolean;
  hideStateIcon?: boolean;
  onChange?: (value: string, callback?: (val: string) => void) => void;
  onEnterPressed?: () => void;
  onBlur?: () => void;
};

const InputField = ({
  id,
  name,
  placeholder,
  label,
  subText,
  helperText,
  link,
  disabled,
  inputState,
  value,
  type = 'text',
  className = '',
  icon,
  isRequired,
  floatLabel,
  hideStateIcon,
  onChange,
  onEnterPressed,
  onBlur, // icon,
}: Props) => {
  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && onEnterPressed) {
      onEnterPressed();
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (onChange) {
      onChange(event.target.value);
    }
  };

  const handleBlur = () => {
    if (onBlur) {
      onBlur();
    }
  };

  const getPlaceholderText = () => {
    const text = placeholder || label || '';
    return isRequired && text ? `${text}*` : text;
  };

  const baseProps = {
    id,
    disabled,
    name: name || id,
    placeholder: getPlaceholderText(),
    className: clsx(
      `body-lg no-spinner peer w-full rounded-28 border border-[#A8A9AE] bg-primary-input-full
      px-16 py-8 ${getOutlineColor(inputState?.type)}`,
      className,
      {
        'pl-40': icon,
        'h-56': type !== 'textarea',
      }
    ),
    value,
    onChange: handleChange,
    onKeyPress: handleKeyPress,
    onBlur: handleBlur,
  };

  const inputProps = { ...baseProps, type };
  const textareaProps = { ...baseProps, rows: 3 };
  return (
    <InputWrapper
      id={id}
      subText={subText}
      helperText={helperText}
      link={link}
      inputState={inputState}
      hideStateIcon={hideStateIcon}
    >
      <div className="group relative">
        <AnimatePresence>
          {floatLabel && value && (
            <motion.label
              initial={{ opacity: 0, y: 0 }}
              animate={{ opacity: 1, y: -10 }}
              transition={{ duration: 0.2 }}
              exit={{ opacity: 0, y: 0 }}
              htmlFor={id}
              className="bg-white pointer-events-none absolute -top-12 left-4 translate-y-[-50%] select-none px-12 text-sm text-primary-faded-text"
            >
              {label}
              {isRequired && <span>*</span>}
            </motion.label>
          )}
        </AnimatePresence>
        {type === 'textarea' ? (
          <textarea {...textareaProps} />
        ) : icon ? (
          <div className={`flex items-center`}>
            <div className="absolute ml-8">
              <SearchIcon color="#181818" />
            </div>
            <input {...inputProps} />
          </div>
        ) : (
          <input {...inputProps} />
        )}
        {!floatLabel && !!value && type !== 'textarea' && (
          <div className="pl-3 collapse absolute -inset-y-16 left-16 flex cursor-pointer items-center peer-focus:visible">
            <span className="body-lg pointer-events-none select-none text-[transparent]">{value}</span>
            {label && <span className="pointer-events-none select-none text-primary-faded-text">{`| ${label}`}</span>}
          </div>
        )}
        {inputState?.type === 'error' && (
          <div className="pl-3 absolute inset-y-0 right-12 flex cursor-pointer items-center">
            <TooltipComponent
              text={inputState?.message || 'Gildi er ekki í réttu formi'}
              placement="top"
              color="primary-slate"
            >
              <Error color="#A8A9AE" />
            </TooltipComponent>
          </div>
        )}
        {onChange && !!value && inputState?.type !== 'error' && (
          <div
            className="pl-3 absolute inset-y-0 right-12 flex cursor-pointer items-center"
            onClick={() => onChange('')}
          >
            <CloseIcon color="#181818" size="18" />
          </div>
        )}
      </div>
    </InputWrapper>
  );
};

export default InputField;
